import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ArrowLeft, ExternalLink, Download, RefreshCw, Save } from 'lucide-react';
import * as XLSX from 'xlsx';
import AdminPayoutPanel from './AdminPayoutPanel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const isPoolPayment = (paymentType) => ['Gelir Havuzu', 'Bütçe Havuzu', 'Gelir Payı', 'Bütçe Payı'].includes(paymentType);
const isPaidCampaign = (paymentType) => !!paymentType && paymentType !== 'Gönüllü';
const formatNumber = (value, digits = 2) => {
  const n = Number(value ?? 0);
  if (!Number.isFinite(n)) return '-';
  return n.toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits });
};
const buildFormulaText = (config = {}) => {
  return `Rj = R0 + a*A0*(Sj/S0)^α*h_erişim + (1-a)*B0*(Cj/C0)^β*h_performans. Hakediş = max(havuz payı, R0).`;
};

// Same defaults as backend/pricing.py DEFAULT_TASK_PRICING_CONFIG.
const TASK_PRICING_DEFAULTS = { A0: 0, B0: 0, a: 0.5, S0: 10000, C0: 100, alpha: 0.5, beta: 0.5 };
const QUALITY_DEFAULTS = { lambda: 0.5, gamma: 1 };
const fmt = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n.toLocaleString('tr-TR', { maximumFractionDigits: 4 }) : '-';
};
const numberOr = (value, fallback) => {
  const n = Number(value);
  return value === null || value === undefined || value === '' || !Number.isFinite(n) ? fallback : n;
};

const qualityText = (label, quality = {}) => {
  const kpis = (quality.kpis || []).filter((kpi) => kpi && kpi.name);
  if (kpis.length === 0) return `h_${label} = 1 (KPI tanımlı değil)`;
  const lambda = numberOr(quality.lambda, QUALITY_DEFAULTS.lambda);
  const gamma = numberOr(quality.gamma, QUALITY_DEFAULTS.gamma);
  const weights = kpis.map((kpi) => `${kpi.name} %${fmt(kpi.weight)}`).join(', ');
  return `h_${label} = (1 + ${fmt(lambda)} × Q_${label})^${fmt(gamma)} · KPI: ${weights}`;
};

// The main formula with this campaign's numbers filled in, one block per task.
function FilledFormula({ tasks, poolAmount }) {
  if (!tasks.length) return null;
  const pool = poolAmount == null ? 'Havuz' : `${fmt(poolAmount)} TL`;
  return (
    <div className="mt-3 space-y-3 border-t border-brand-line pt-3">
      <div className="text-xs font-medium uppercase tracking-wide text-gray-500">Bu kampanyanın değerleriyle</div>
      {tasks.map((task) => {
        const c = { ...TASK_PRICING_DEFAULTS, ...(task.pricing_config || {}) };
        const a = numberOr(c.a, TASK_PRICING_DEFAULTS.a);
        const R0 = numberOr(task.payment_amount, 0);
        const term = (key) => fmt(numberOr(c[key], TASK_PRICING_DEFAULTS[key]));
        return (
          <div key={task.id} data-testid={`filled-formula-${task.id}`}>
            <div className="font-medium text-gray-900">{task.title}</div>
            <div className="font-mono text-xs leading-relaxed">
              <div>
                Rj = {fmt(R0)} + {fmt(a)} × {term('A0')} × (Sj / {term('S0')})^{term('alpha')} × h_erişim
                {' + '}{fmt(1 - a)} × {term('B0')} × (Cj / {term('C0')})^{term('beta')} × h_performans
              </div>
              <div>{qualityText('erişim', c.reach_quality)}</div>
              <div>{qualityText('performans', c.perf_quality)}</div>
              <div>Hakediş = max({pool} × Rj / ΣR, {fmt(R0)} TL)</div>
            </div>
          </div>
        );
      })}
      <div className="text-xs text-gray-500">
        Sj: gönderim anındaki takipçi sayısı · Cj: geçerli (tekil) tıklama · Q: KPI kalite skoru (-1 ile 1 arası) · ΣR: tüm onaylı gönderimlerin puan toplamı
      </div>
    </div>
  );
}

export default function AdminCampaignReport() {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const { session } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ submissions: [], campaign_info: {} });
  const [pricing, setPricing] = useState(null);
  const [revenueAmount, setRevenueAmount] = useState('');
  const [pricingSaving, setPricingSaving] = useState(false);
  const [metricDrafts, setMetricDrafts] = useState({});
  const [tasks, setTasks] = useState([]);

  const fetchPricing = useCallback(async () => {
    if (!session?.access_token) return null;
    const response = await fetch(`${BACKEND_URL}/api/admin/campaigns/${campaignId}/pricing-preview`, {
      headers: { 'Authorization': `Bearer ${session.access_token}` }
    });
    if (!response.ok) return null;
    const result = await response.json();
    setPricing(result);
    setRevenueAmount(result?.summary?.revenue_amount ?? '');
    return result;
  }, [campaignId, session?.access_token]);

  const fetchData = useCallback(async () => {
    if (!session?.access_token) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/campaigns/${campaignId}/export`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });
      
      if (!response.ok) throw new Error('Failed to fetch data');
      
      const result = await response.json();
      setData(result);
      if (isPoolPayment(result.campaign_info?.payment_type)) {
        const tasksResponse = await fetch(`${BACKEND_URL}/api/campaigns/${campaignId}/tasks`);
        setTasks(tasksResponse.ok ? await tasksResponse.json() : []);
        await fetchPricing();
      } else {
        setPricing(null);
      }
    } catch (error) {
      console.error('Error fetching campaign report:', error);
      toast.error('Rapor yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  }, [session?.access_token, campaignId, fetchPricing]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const drafts = {};
    (pricing?.rows || []).forEach((row) => {
      drafts[row.submission_id] = {
        reach_kpis: row.reach_kpis || [],
        perf_kpis: row.perf_kpis || [],
      };
    });
    setMetricDrafts(drafts);
  }, [pricing]);

  const exportToExcel = () => {
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(data.submissions);
    XLSX.utils.book_append_sheet(wb, ws, 'Katılımcılar');
    if (pricing?.rows?.length) {
      const payoutRows = pricing.rows.map((row) => ({
        katilimci_no: row.participant_no,
        eposta: row.email,
        ad_soyad: row.full_name,
        gorev: row.task_title,
        platform: row.platform,
        gorev_baz_tl: row.task_base_amount ?? row.task_base_points,
        takipci: row.followers,
        tiklama: row.conversion,
        ham_puan: row.raw_score,
        gorev_puani: row.submission_score,
        kullanici_toplam_puan: row.user_total_score,
        kampanya_toplam_puan: row.campaign_total_score,
        pay_yuzde: row.share_percent,
        havuz_pay_tl: row.pool_share_amount,
        minimum_tl: row.minimum_payout_amount,
        tl: row.payout_amount,
        durum: row.payout_status,
      }));
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(payoutRows), 'Hakediş');
    }
    XLSX.writeFile(wb, `${data.campaign_info.kampanya_adi}_rapor.xlsx`);
    toast.success('Excel indirildi');
  };

  const handleSaveRevenue = async () => {
    if (!session?.access_token) return;
    setPricingSaving(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/campaigns/${campaignId}/pricing-revenue`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({ revenue_amount: revenueAmount === '' ? null : Number(revenueAmount) })
      });
      if (!response.ok) throw new Error('Gelir kaydedilemedi');
      await fetchPricing();
      toast.success('Gelir tutarı kaydedildi');
    } catch (error) {
      console.error('Revenue save error:', error);
      toast.error('Gelir tutarı kaydedilemedi');
    } finally {
      setPricingSaving(false);
    }
  };

  const updateMetricDraft = (submissionId, key, index, value) => {
    setMetricDrafts(prev => {
      const current = prev[submissionId] || { reach_kpis: [], perf_kpis: [] };
      const values = [...(current[key] || [])];
      values[index] = value === '' ? '' : Number(value);
      return {
        ...prev,
        [submissionId]: {
          ...current,
          [key]: values,
        },
      };
    });
  };

  const saveMetrics = async (row) => {
    if (!session?.access_token) return;
    const draft = metricDrafts[row.submission_id] || { reach_kpis: [], perf_kpis: [] };
    setPricingSaving(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/campaigns/${campaignId}/pricing-metrics/${row.submission_id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          reach_kpis: (draft.reach_kpis || []).map(Number),
          perf_kpis: (draft.perf_kpis || []).map(Number),
        })
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'KPI değerleri kaydedilemedi');
      }
      await fetchPricing();
      toast.success('KPI değerleri kaydedildi');
    } catch (error) {
      console.error('Metrics save error:', error);
      toast.error(error.message || 'KPI değerleri kaydedilemedi');
    } finally {
      setPricingSaving(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'İnceleniyor': <Badge className="bg-brand-yellow text-brand-ink">İnceleniyor</Badge>,
      'Tamamlandı': <Badge className="bg-green-100 text-green-800">Tamamlandı</Badge>,
      'Reddedildi': <Badge className="bg-red-100 text-red-800">Reddedildi</Badge>,
    };
    return statusMap[status] || <Badge>{status}</Badge>;
  };

  return (
    <div data-testid="admin-campaign-report">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin/campaigns')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Geri
          </Button>
          <div>
            <h2 className="font-brand text-2xl text-brand-ink">Kampanya Raporu</h2>
            {!loading && (
              <p className="text-sm text-gray-500 mt-1">
                {data.campaign_info.kampanya_adi}
              </p>
            )}
          </div>
        </div>
        <Button onClick={exportToExcel} disabled={loading || data.submissions.length === 0}>
          <Download className="w-4 h-4 mr-2" />
          Excel İndir
        </Button>
      </div>

      {/* Stats */}
      {!loading && (
        <div className="grid grid-cols-3 gap-4 mb-6">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Toplam Katılımcı</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{data.campaign_info.toplam_katilimci || 0}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Toplam Gönderim</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{data.submissions.length}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Toplam Görev</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{data.campaign_info.toplam_gorev || 0}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {!loading && isPaidCampaign(data.campaign_info?.payment_type) && session?.access_token && (
        <AdminPayoutPanel campaignId={campaignId} accessToken={session.access_token} onChanged={() => isPoolPayment(data.campaign_info?.payment_type) && fetchPricing()} />
      )}

      {!loading && pricing && (
        <div className="mb-6 rounded-lg border border-brand-line bg-white p-4">
          <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
            <div>
              <h3 className="font-semibold text-lg text-gray-900">Havuz Hakedişleri</h3>
              <p className="text-sm text-gray-500">
                Rj görev skoru olarak hesaplanır; kampanya bütçesi/geliri toplam görev skoruna göre dağıtılır ve baz TL minimum garanti olarak korunur.
              </p>
            </div>
            <div className="flex items-end gap-2">
              {pricing.summary?.pool_amount_source === 'revenue' && (
                <div className="space-y-1">
                  <Label className="text-xs">Kampanya Geliri (TL)</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    className="w-40"
                    value={revenueAmount}
                    disabled={pricing.summary?.finalized}
                    onChange={(e) => setRevenueAmount(e.target.value)}
                  />
                </div>
              )}
              {pricing.summary?.pool_amount_source === 'revenue' && (
                <Button variant="outline" onClick={handleSaveRevenue} disabled={pricingSaving || pricing.summary?.finalized}>
                  <Save className="w-4 h-4 mr-2" />
                  Geliri Kaydet
                </Button>
              )}
              <Button variant="outline" onClick={fetchPricing} disabled={pricingSaving}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Yenile
              </Button>
            </div>
          </div>

          {pricing.errors?.length > 0 && (
            <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {pricing.errors.join(' • ')}
            </div>
          )}

          <div className="grid grid-cols-4 gap-4 mb-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">Kampanya Toplam Puanı</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-xl font-bold">{formatNumber(pricing.summary?.campaign_total_score, 2)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">Havuz Tutarı</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-xl font-bold">
                  {pricing.summary?.pool_amount == null ? '-' : `${formatNumber(pricing.summary.pool_amount, 2)} TL`}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">Dağıtılan</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-xl font-bold">
                  {pricing.summary?.total_payout == null ? '-' : `${formatNumber(pricing.summary.total_payout, 2)} TL`}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">Durum</CardTitle>
              </CardHeader>
              <CardContent>
                <Badge className={pricing.summary?.finalized ? 'bg-green-100 text-green-800' : 'bg-brand-yellow-soft text-brand-ink'}>
                  {pricing.summary?.finalized ? 'Kesinleşmiş' : 'Tahmini'}
                </Badge>
              </CardContent>
            </Card>
          </div>

          <div className="mb-4 rounded-md border border-brand-line bg-brand-yellow-soft/60 p-3 text-sm text-gray-700">
            {buildFormulaText(pricing.config)}
            <FilledFormula tasks={tasks} poolAmount={pricing.summary?.pool_amount} />
          </div>

          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Katılımcı</TableHead>
                  <TableHead>Görev</TableHead>
                  <TableHead className="text-right">Baz TL</TableHead>
                  <TableHead className="text-right">Takipçi</TableHead>
                  <TableHead className="text-right">Geçerli Click</TableHead>
                  <TableHead>Kalite KPI</TableHead>
                  <TableHead className="text-right">Görev Puanı</TableHead>
                  <TableHead className="text-right">Kullanıcı Toplam</TableHead>
                  <TableHead className="text-right">Pay</TableHead>
                  <TableHead className="text-right">Pay TL</TableHead>
                  <TableHead className="text-right">Min TL</TableHead>
                  <TableHead className="text-right">TL</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pricing.rows.map((row) => (
                  <TableRow key={row.submission_id}>
                    <TableCell>
                      <div className="text-sm font-medium">{row.full_name || '-'}</div>
                      <div className="text-xs text-gray-500">{row.email}</div>
                    </TableCell>
                    <TableCell className="text-sm">{row.task_title}</TableCell>
                    <TableCell className="text-right">{formatNumber(row.task_base_amount ?? row.task_base_points, 2)}</TableCell>
                    <TableCell className="text-right">{formatNumber(row.followers, 0)}</TableCell>
                    <TableCell className="text-right">{formatNumber(row.conversion, 0)}</TableCell>
                    <TableCell className="min-w-[260px]">
                      {[
                        ['reach_quality', 'reach_kpis', 'Erişim'],
                        ['perf_quality', 'perf_kpis', 'Performans'],
                      ].map(([configKey, valuesKey, label]) => {
                        const kpis = row.pricing_config?.[configKey]?.kpis || [];
                        if (kpis.length === 0) return null;
                        return (
                          <div key={valuesKey} className="mb-2 space-y-1">
                            <div className="text-xs font-medium text-gray-600">{label}</div>
                            {kpis.map((kpi, index) => (
                              <div key={`${valuesKey}-${index}`} className="flex items-center gap-2">
                                <Label className="w-28 truncate text-xs">{kpi.name}</Label>
                                <Input
                                  type="number"
                                  step="0.01"
                                  className="h-8 w-24"
                                  disabled={pricing.summary?.finalized}
                                  value={metricDrafts[row.submission_id]?.[valuesKey]?.[index] ?? ''}
                                  onChange={(e) => updateMetricDraft(row.submission_id, valuesKey, index, e.target.value)}
                                />
                              </div>
                            ))}
                          </div>
                        );
                      })}
                      {((row.pricing_config?.reach_quality?.kpis || []).length > 0 || (row.pricing_config?.perf_quality?.kpis || []).length > 0) ? (
                        <Button size="sm" variant="outline" onClick={() => saveMetrics(row)} disabled={pricingSaving || pricing.summary?.finalized}>
                          Kaydet
                        </Button>
                      ) : (
                        <span className="text-xs text-gray-400">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">{formatNumber(row.submission_score, 2)}</TableCell>
                    <TableCell className="text-right">{formatNumber(row.user_total_score, 2)}</TableCell>
                    <TableCell className="text-right">%{formatNumber(row.share_percent, 2)}</TableCell>
                    <TableCell className="text-right">{row.pool_share_amount == null ? '-' : `${formatNumber(row.pool_share_amount, 2)} TL`}</TableCell>
                    <TableCell className="text-right">{row.minimum_payout_amount == null ? '-' : `${formatNumber(row.minimum_payout_amount, 2)} TL`}</TableCell>
                    <TableCell className="text-right">
                      {row.payout_amount == null ? '-' : `${formatNumber(row.payout_amount, 2)} TL`}
                      <div className="text-xs text-gray-500">{row.payout_status}</div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="space-y-4">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </div>
      ) : data.submissions.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <p className="text-gray-600">Henüz katılımcı bulunmuyor</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-24">Katılımcı No</TableHead>
                <TableHead>E-posta</TableHead>
                <TableHead>Ad Soyad</TableHead>
                <TableHead>Görev</TableHead>
                <TableHead>Durum</TableHead>
                <TableHead className="text-center">Gönderim Sayısı</TableHead>
                <TableHead>Kazanç</TableHead>
                <TableHead>Link</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.submissions.map((submission, idx) => (
                <TableRow key={idx}>
                  <TableCell className="font-mono text-sm">{submission.katilimci_no}</TableCell>
                  <TableCell className="text-sm">{submission.kullanici_email}</TableCell>
                  <TableCell>{submission.kullanici_adi}</TableCell>
                  <TableCell className="text-sm">{submission.gorev_adi}</TableCell>
                  <TableCell>{getStatusBadge(submission.gorev_durumu)}</TableCell>
                  <TableCell className="text-center">{submission.gonderim_sayisi}</TableCell>
                  <TableCell>{submission.kazanilan_ucret}</TableCell>
                  <TableCell>
                    {submission.yuklenen_link && submission.yuklenen_link !== '-' && (
                      <a
                        href={submission.yuklenen_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-brand-ink font-semibold underline-offset-2 hover:underline flex items-center gap-1"
                      >
                        <ExternalLink className="w-3 h-3" />
                        Görüntüle
                      </a>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
